import type { KpiCardSpec } from '../kpis/card-spec';
import { ALL_TIME_DOWNLOADS_SCALAR_CARD } from '../kpis/all-time-downloads-scalar-card';
import { MAU_SCALAR_CARD } from '../kpis/mau-scalar-card';
import { AGE_VERIFICATION_SCALAR_CARD } from '../kpis/age-verification-scalar-card';
import { COUPONS_REDEEMED_SCALAR_CARD } from '../kpis/coupons-redeemed-scalar-card';
import { TOTAL_USERS_SCALAR_CARD } from '../kpis/total-users-scalar-card';
import { EMAIL_OPT_INS_SCALAR_CARD } from '../kpis/email-opt-ins-scalar-card';
import { PROMOTIONS_ACTIVATED_SCALAR_CARD } from '../kpis/promotions-activated-scalar-card';
import { AUTO_REISSUED_PROMOTIONS_SCALAR_CARD } from '../kpis/auto-reissued-promotions-scalar-card';
import { withDashboardLookAll } from '../kpis/with-dashboard-look';

/**
 * Acquisition seed cards, grouped by dashboard section.
 *
 * Cards follow the page-level Period + Region filter, the same Model A the
 * Overview uses, so the toolbar at the top of the page drives every card on it.
 * All-time downloads is the one exception and says so on the card.
 *
 * Four sections: the Pulse headline row, the downloads chart, a Detail
 * section pairing onboarding-state with age-gated, and the coupon breakdown.
 * The Featured row that used to duplicate Pulse is gone: one headline row,
 * with Active users (MAU) folded into it.
 */

/**
 * Pulse: the page's one headline row of hero tiles.
 *
 * The mockup shows Registration rate here instead
 * (`profile-creation-conversion`/`conversionRate`). Not shipped: that
 * source's denominator (`installs`) comes from the app stores, and Google
 * Play reports roughly a week behind, so on a period ending today (this
 * page's default) the rate would read null or overstated. A tile that is
 * systematically wrong on the page's own default view is worse than a tile
 * the mockup didn't ask for, so this slot keeps the New users count instead.
 * The "carries no store-fed rate" test below is what makes that mistake
 * catchable if it ever comes back.
 */
export const PULSE_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        ALL_TIME_DOWNLOADS_SCALAR_CARD,
        MAU_SCALAR_CARD,
        {
            // A count off the dedicated new-users source, not the install-to-
            // registration rate the mockup shows in this slot: that rate's
            // denominator comes from the app stores, and Google Play reports
            // about a week behind, so on a period ending today (this page's
            // default) the rate reads null or overstated.
            title: 'New users',
            source: 'new-users',
            form: 'number',
            metric: 'newUsers',
            size: 'sm',
            description:
                'App accounts created during the selected period, counted on their creation date, following the page Period and Region filters with a period-over-period delta. A province is written when a user completes profile setup, not when they register, so any Region selection counts only the accounts that got that far and reads below the national total.',
        },
        AGE_VERIFICATION_SCALAR_CARD,
        // The row's two standing counts, which answer "how many are there now"
        // where the four tiles above answer "how many in the period".
        TOTAL_USERS_SCALAR_CARD,
        EMAIL_OPT_INS_SCALAR_CARD,
    ],
    'blue'
);

/**
 * The downloads chart, in its own section below the headline row.
 *
 * The only downloads CHART in the dashboard, and the only reader of the
 * period-scoped `app-downloads` source left on the headline pages: the tiles
 * beside and above it read the lifetime total instead
 * (`ALL_TIME_DOWNLOADS_SCALAR_CARD`, which used to sit in this section).
 */
export const DOWNLOADS_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // A chart rather than a total: a total hides that the two stores report
            // at different speeds, so a period ending today reads as a slump rather
            // than a late Google Play report. The legend carries both totals.
            title: 'Downloads',
            source: 'app-downloads',
            form: 'downloads',
            metric: 'total',
            size: 'lg',
            description:
                'Daily installs of the Couche-Tard and Circle K apps, one line per store, over the selected period. Each line ends where its store has reported to: Google Play usually runs about a week behind the App Store, so the Android line stops earlier.',
        },
    ],
    'blue'
);

/** Onboarding: the onboarding-state cohort as absolute counts. */
export const ONBOARDING_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // One column per state, registration included as the first column.
            // The Overview carries the same source as horizontal bars (shares of
            // `registered`); this reading answers "how many accounts", the
            // Overview's answers "what share".
            //
            // No step-over-step delta, and that is the point: `columnsMetrics`
            // emits none, and the states are not nested. A skipped optional step
            // still completes onboarding, so `complete` can exceed `segmentation`,
            // and age verification is a standalone activation flow rather than a
            // step after phone confirmation. A step delta here would print a rise
            // in the middle of a descending chart and invite a drop-off reading the
            // data cannot support.
            //
            // Install is absent on purpose: store installs have no join key to an
            // account and report on a brand grain, so a first column from the
            // Downloads source would carry a different population than every
            // column after it. Downloads keeps its own card above.
            title: 'From registration to onboarding complete',
            source: 'onboarding-state',
            form: 'columns',
            size: 'md',
            description:
                'Accounts created in the selected period, and how many of them hold each onboarding state today. Counts, not a funnel: the states are independent, so a later column can stand higher than an earlier one. Follows the page Period filter; national only, so the Region filter does not narrow it.',
        },
    ],
    'blue'
);

/** Age-gated: the age-gating rate, broken down by business unit over time. */
export const AGE_GATED_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // One multiline series per business unit rather than a single
            // national trend: the mockup's own chart is the province partition
            // folded server-side (see the design doc, Decision 1), not a
            // client-side fan-out, so no province map needs mirroring here.
            title: 'Age-gated acquisition',
            source: 'age-gating',
            form: 'multiline',
            metric: 'ageGatedRate',
            breakdownBy: 'businessUnit',
            size: 'md',
            description:
                "Share of each business unit's new users who completed age verification, plotted daily over the selected period, one line per unit: Quebec, Ontario, Atlantic, and Western. Each unit's own signup-date counts drive its rate, following the page Period filter.",
        },
    ],
    'blue'
);

/** Coupon: the period's ranking of the most-redeemed coupons.
 *
 * Held one card that needed a page-level coupon selection (available vs
 * reclaimed for one promotion, plus a redemption-rate tile on both headline
 * rows). Both are gone with the selector: `available` and `redemptionRate` are
 * per-coupon by definition, so without a selection there is nothing for them
 * to be about. */
export const COUPON_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            title: 'Coupons issued',
            source: 'coupon-issued',
            form: 'number',
            metric: 'couponsIssued',
            size: 'sm',
            description:
                'Coupons issued in the selected period across the promotions belonging to no campaign, following the page Period and Region filters. RPP and Road to Rewards report their own figures, never numbers to add to this one. Issued is what the business put out, redeemed beside it is what members took, on different dates.',
        },
        COUPONS_REDEEMED_SCALAR_CARD,
        // Both read the one coupon-activation source but rest on different date
        // columns: claimed counts activations created in the period, reissued
        // counts reissues that happened in it.
        PROMOTIONS_ACTIVATED_SCALAR_CARD,
        AUTO_REISSUED_PROMOTIONS_SCALAR_CARD,
        {
            // Same axis rules as the business-unit donuts below: a partition, not
            // a ranking.
            title: 'Redeemed by category',
            source: 'coupon-redeemed',
            form: 'pie',
            metric: 'couponsRedeemed',
            breakdownBy: 'category',
            size: 'md',
            description:
                'The same coupons redeemed, split by promotion category: the ten most-redeemed, then Other for the rest and Uncategorized for promotions carrying none. The rows sum to the headline count. A category nobody redeemed has no slice rather than a zero one, so this shows which categories moved, not which exist.',
        },
        {
            // The endpoint takes a province filter, so this card follows the page
            // Region filter and shows the ranking inside the selected region.
            // `barBaseInHeader`: every bar shares one denominator (the period's
            // total redemptions, carried on each row as `base`), so the total shows
            // once in the title instead of repeating on all five rows.
            title: 'Top coupons',
            source: 'coupon-top-redeemed',
            form: 'bars',
            breakdownBy: 'promotion',
            size: 'md',
            barBaseInHeader: true,
            description:
                "The period's most-redeemed coupons, highest first, each bar its share of all redemptions. Counts the promotions belonging to no campaign and excludes RPP and Road to Rewards. A truncated top five, so the bars do not sum to 100%: the remainder is the rest of the catalog. Follows the page Period and Region filters.",
        },
        {
            // Same axis and same order as Redeemed by business unit below, so the
            // two donuts can be read against each other slice by slice.
            title: 'Issued by business unit',
            source: 'coupon-issued',
            form: 'pie',
            metric: 'couponsIssued',
            breakdownBy: 'businessUnit',
            size: 'md',
            description:
                'The same coupons issued, split across the four business units. The rows fold the same buckets as the headline count, so they sum to it. Unknown collects the issues whose account carries no province.',
        },
        {
            title: 'Redeemed by business unit',
            source: 'coupon-redeemed',
            form: 'pie',
            metric: 'couponsRedeemed',
            breakdownBy: 'businessUnit',
            size: 'md',
            description:
                'The same coupons redeemed, split across the four business units, on the same axis as Issued by business unit above. A unit redeeming a larger share than it was issued is the comparison this pair exists for.',
        },
    ],
    'blue'
);

/**
 * Coupon detail: the cards that follow the one coupon picked in the section
 * header, so each carries `pageCoupon` and holds its fetch until a coupon is
 * chosen. The Coupons section above never does.
 */
export const COUPON_DETAIL_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            title: 'Coupons issued',
            source: 'coupon-issued',
            form: 'number',
            metric: 'couponsIssued',
            size: 'sm',
            privateCouponsOnly: true,
            description:
                'Coupons issued for the coupon chosen above, counted as grants created in the selected period, following the page Period and Region filters with a period-over-period delta. The selector lists only promotions belonging to no campaign, so RPP and Road to Rewards coupons never appear here.',
        },
        {
            title: 'Coupons redeemed',
            source: 'coupon-redeemed',
            form: 'number',
            metric: 'couponsRedeemed',
            size: 'sm',
            appTrackedCouponsOnly: true,
            description:
                'Redemptions of the coupon chosen above, counted on the date each was redeemed in the selected period, following the page Period and Region filters with a period-over-period delta. The selector lists only promotions belonging to no campaign, so RPP and Road to Rewards coupons never appear here.',
        },
        {
            title: 'Redemption rate',
            source: 'coupon-issued',
            form: 'rate',
            metric: 'couponRedemptionRate',
            size: 'sm',
            hideTrend: true,
            privateCouponsOnly: true,
            appTrackedCouponsOnly: true,
            description:
                'Of the coupons issued in the selected period, the share already redeemed, for the coupon chosen above. Counts promotions belonging to no campaign. Recent issues read low, having had less time to be used, and an automatically reissued coupon leaves the redeemed share. Follows the page Period and Region filters.',
        },
        {
            title: 'Redeemed by business unit',
            source: 'coupon-redeemed',
            form: 'pie',
            metric: 'couponsRedeemed',
            breakdownBy: 'businessUnit',
            size: 'sm',
            legendBeside: 'compact',
            appTrackedCouponsOnly: true,
            description:
                'The same redemptions of the chosen coupon, split across the four business units. The rows fold the same buckets as the headline count, so they sum to it. Only promotions belonging to no campaign are listed, and the page Period and Region filters apply.',
        },
    ],
    'blue'
);
